import { AlertCircle, type LucideIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/utils';

/**
 * A form-level message with a recovery action, announced to screen readers.
 *
 * Extracted from the auth form's `AuthError`, which is where the two
 * decisions in here were measured.
 *
 * **The text is `text-foreground`, not the tone colour**, and that is a
 * measurement rather than a preference: on a `/10` tint `--destructive` gives
 * 4.01:1 in light, which fails AA and which axe catches, against 6.98:1 in
 * dark. The border and icon carry "this is an error", which is what colour is
 * good at; the sentence carries the words, which is what contrast is for.
 * Measured after: 14.84:1 light, 13.69:1 dark.
 *
 * **`role='alert'` plus an optional action.** An error without an escape route
 * is a dead end - see `classifyAuthError` in the web app for the case that
 * motivated it.
 */
/**
 * Exported because `Toaster` renders the same surface. That component used to
 * restate every tone pair here by hand — and restated the `text-foreground`
 * rationale above as a second comment — so the two could disagree about what
 * an error looks like. One `cva`, two consumers.
 */
export const alertVariants = cva(
  'flex gap-2.5 rounded-md border px-3 py-2.5 text-body text-foreground',
  {
    variants: {
      tone: {
        error:
          'border-destructive/30 bg-destructive/10 [&_svg]:text-destructive',
        warning:
          'border-warning/30 bg-warning/10 [&_svg]:text-warning-emphasis',
        info: 'border-info/30 bg-info/10 [&_svg]:text-info',
        success:
          'border-success/30 bg-success/10 [&_svg]:text-success-emphasis',
        /** Untinted. A toast with no status — `toast('Saved draft')`. */
        neutral: 'border-border bg-card',
      },
    },
    defaultVariants: { tone: 'error' },
  }
);

export interface InlineAlertProps extends VariantProps<typeof alertVariants> {
  message: string;
  /** Label for the inline recovery action. Rendered only with `onAction`. */
  actionLabel?: string;
  onAction?: () => void;
  icon?: LucideIcon;
  className?: string;
}

export function InlineAlert({
  message,
  actionLabel,
  onAction,
  tone,
  icon: Icon = AlertCircle,
  className,
}: InlineAlertProps) {
  return (
    <div role='alert' className={cn(alertVariants({ tone }), className)}>
      <Icon className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
      <p>
        {message}{' '}
        {actionLabel && onAction && (
          <button
            type='button'
            onClick={onAction}
            // `tap-target` (apps/web globals.css): a 24px line inside the
            // sentence, tapped as 44 on a phone - it is the way out of the
            // error ("Reset your password"), and the text around it is not a
            // target, so the larger area overlaps nothing tappable.
            className='tap-target font-medium underline underline-offset-4'
          >
            {actionLabel}
          </button>
        )}
      </p>
    </div>
  );
}
